<script setup lang="ts">
/**
 * HLOS-140 — ConversationStatus (loading + error compartilhado).
 *
 * Usado por ConversationInit (boot) e ConversationChat (runtime).
 * Centraliza o markup de status idêntico que HLOS-138 duplicou nos dois.
 *
 * **Constitucional:**
 *   - CL-09: `aria-live="polite"` no loading (screen readers)
 *   - CC-05: error com saída "encerrar e tentar mais tarde" (zero penalidade)
 *   - CC-04: sem fake-progress
 *   - Princípio XI: copy neutra, sem menção a provedor
 *
 * **Ref:** HLOS-140, HLOS-138, HLOS-15.
 */
defineProps<{
  /** Exibe o indicador de loading. */
  loading: boolean;
  /** Exibe o fallback de erro (com ação de encerrar). */
  errored: boolean;
  /**
   * Contexto do loading: 'boot' (Init — cold-start Pyodide) vs 'runtime'
   * (Chat — processamento de turno). Define o data-testid: boot-loading
   * vs kernel-loading (specs distinguem os dois fluxos).
   */
  variant?: "boot" | "runtime";
}>();

const emit = defineEmits<{
  (e: "end"): void;
}>();
</script>

<template>
  <!-- HLOS-15: loading. Sem fake-progress (CC-04). Mensagem sem provedor. -->
  <p
    v-if="loading"
    class="hlos-conversation__loading"
    role="status"
    aria-live="polite"
    :data-testid="variant === 'boot' ? 'boot-loading' : 'kernel-loading'"
  >
    Preparando Companheiro… (primeira vez pode levar alguns segundos)
  </p>

  <!--
    HLOS-15: erro técnico. Fallback gracioso (CC-05: zero penalidade).
    Mensagem NÃO menciona provedor (Princípio XI) e oferece saída.
  -->
  <p
    v-else-if="errored"
    class="hlos-conversation__error"
    role="alert"
    data-testid="kernel-error"
  >
    O companheiro não pôde ser carregado agora. Você pode
    <button
      type="button"
      class="hlos-conversation__error-action"
      data-testid="kernel-error-end"
      @click="emit('end')"
    >
      encerrar e tentar mais tarde
    </button>.
  </p>
</template>

<style src="./conversation-status.css" scoped></style>
